import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  MenuItem,
  TextField,
  Divider,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useAppSelector, useAppDispatch } from "../../../../redux/store";
import { chargeCart } from "../../../../redux/slices/invoicesSlice";
import { openProjectDrawer } from "../../../../redux/slices/projectsSlice";

// Amount left to pay for a pending invoice. Pending invoices carry
// calculatedAmountDue (amountDue - scheduledAmount) from the server; fall
// back to deriving it so the dialog works regardless of payload shape.
const payableAmount = (invoice: any) =>
  invoice?.calculatedAmountDue ??
  (invoice?.amountDue != null && invoice?.scheduledAmount != null
    ? invoice.amountDue - invoice.scheduledAmount
    : invoice?.amount ?? 0);

const formatMoney = (value: number) =>
  value.toLocaleString("en-US", { style: "currency", currency: "USD" });

interface CartPaymentDialogProps {
  open: boolean;
  invoices: any[];
  project: any;
  onClose: () => void;
}

const CartPaymentDialog = ({
  open,
  invoices,
  project,
  onClose,
}: CartPaymentDialogProps) => {
  const dispatch = useAppDispatch();
  const { cartChargeStatus } = useAppSelector((state) => state.invoices) as any;

  const bankAccounts = project?.bankAccounts || [];
  const [accountId, setAccountId] = useState<string>("");
  const [authorized, setAuthorized] = useState(false);
  // What to take from each invoice, keyed by invoice id. Prefilled with the
  // full balance; the client may lower it to pay part of an invoice.
  const [amounts, setAmounts] = useState<Record<string, string>>({});

  // Reset the selected account and amounts whenever the dialog opens for a
  // project or the basket changes. Keying on the ids (not the array identity)
  // keeps a parent re-render from wiping an amount the client is typing.
  const invoiceKey = invoices.map((inv) => inv.id).join("|");
  useEffect(() => {
    if (!open) return;
    setAccountId(bankAccounts[0]?.accountID || "");
    setAuthorized(false);
    setAmounts(
      Object.fromEntries(
        invoices.map((inv) => [inv.id, payableAmount(inv).toFixed(2)]),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project?.providerId, invoiceKey]);

  const selectedAccount = bankAccounts.find(
    (account: any) => account.accountID === accountId,
  );
  const last4 = String(selectedAccount?.accountNumber || "")
    .replace(/\D/g, "")
    .slice(-4);

  const rows = useMemo(
    () =>
      invoices.map((inv) => {
        const max = Math.max(0, payableAmount(inv) || 0);
        const raw = amounts[inv.id] ?? max.toFixed(2);
        const parsed = Number(raw);
        const valid =
          Number.isFinite(parsed) &&
          parsed > 0 &&
          parsed <= max + 0.005 &&
          max > 0;
        return {
          invoice: inv,
          max,
          raw,
          value: valid ? Math.round(parsed * 100) / 100 : 0,
          valid,
        };
      }),
    [invoices, amounts],
  );

  const total = rows.reduce((sum, row) => sum + row.value, 0);
  const amountsValid = rows.length > 0 && rows.every((row) => row.valid);

  const loading = cartChargeStatus === "loading";

  const handleConfirm = useCallback(async () => {
    if (
      !project?.providerId ||
      !accountId ||
      rows.length === 0 ||
      !amountsValid
    )
      return;
    try {
      await dispatch(
        chargeCart({
          customerId: project.providerId,
          accountId,
          payments: rows.map((row) => ({
            invoiceId: row.invoice.id,
            amount: row.value,
          })),
        }),
      ).unwrap();
    } catch {
      // Error surfaces via the snackbar watcher in the parent.
    }
  }, [dispatch, project?.providerId, accountId, rows, amountsValid]);

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      component="form"
      fullWidth
      onClose={loading ? undefined : onClose}
      sx={{
        "& .MuiDialog-paper": { borderRadius: 0, p: 1 },
        "& .MuiOutlinedInput-root": { borderRadius: 0 },
      }}
    >
      <DialogTitle>Pay Selected Invoices</DialogTitle>
      <DialogContent>
        {invoices.length === 0 ? (
          <Alert severity="info">No invoices selected.</Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {project?.name} · {invoices.length}{" "}
              {invoices.length === 1 ? "invoice" : "invoices"}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              Pay the full balance or enter a smaller amount for any invoice.
            </Typography>
            <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
              {rows.map((row) => (
                <Box
                  key={row.invoice.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1.5,
                    py: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.invoice.invoiceNumber || row.invoice.id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Balance {formatMoney(row.max)}
                    </Typography>
                  </Box>
                  <TextField
                    size="small"
                    type="number"
                    value={row.raw}
                    onChange={(event) =>
                      setAmounts((prev) => ({
                        ...prev,
                        [row.invoice.id]: event.target.value,
                      }))
                    }
                    onBlur={() => {
                      const parsed = Number(row.raw);
                      if (Number.isFinite(parsed) && parsed > row.max) {
                        setAmounts((prev) => ({
                          ...prev,
                          [row.invoice.id]: row.max.toFixed(2),
                        }));
                      }
                    }}
                    error={!row.valid}
                    disabled={loading || row.max <= 0}
                    slotProps={{
                      htmlInput: {
                        min: "0.01",
                        max: row.max.toFixed(2),
                        step: "0.01",
                        inputMode: "decimal",
                        "aria-label": `Amount to pay for invoice ${
                          row.invoice.invoiceNumber || row.invoice.id
                        }`,
                      },
                    }}
                    sx={{ width: 120 }}
                  />
                </Box>
              ))}
            </Box>
            {!amountsValid && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                Check the amounts: each must be more than $0.00 and no more
                than that invoice's balance.
              </Alert>
            )}
            <Divider sx={{ my: 1.5 }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography sx={{ fontWeight: 700 }}>Total</Typography>
              <Typography sx={{ fontWeight: 700 }}>
                {formatMoney(total)}
              </Typography>
            </Box>

            {bankAccounts.length === 0 ? (
              <Alert severity="warning">
                <Typography variant="body2">
                  No bank account linked to this project. Add one to pay
                  multiple invoices at once.
                </Typography>
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={() => {
                    onClose();
                    dispatch(openProjectDrawer(project));
                  }}
                >
                  Project Settings
                </Button>
              </Alert>
            ) : (
              <Grid container rowSpacing={1.5}>
                <Grid size={12}>
                  <Typography variant="subtitle2">Select Account</Typography>
                  <TextField
                    select
                    fullWidth
                    required
                    size="small"
                    value={accountId}
                    onChange={(e) => setAccountId(e.target.value)}
                    disabled={loading}
                  >
                    {bankAccounts.map((account: any) => (
                      <MenuItem
                        key={account.accountID}
                        value={account.accountID}
                      >
                        {account.accountName} - {account.accountNumber}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            )}

            {accountId && bankAccounts.length > 0 && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  border: "1px solid var(--tpc-200)",
                  backgroundColor: "var(--surface-container-low, #f7f7f7)",
                }}
              >
                <Typography variant="body2">
                  One ACH payment of{" "}
                  <strong>{formatMoney(total)}</strong> from{" "}
                  <strong>
                    {selectedAccount?.accountName || "the selected account"}
                    {last4 ? ` ending ${last4}` : ""}
                  </strong>{" "}
                  will be applied to {invoices.length}{" "}
                  {invoices.length === 1 ? "invoice" : "invoices"} when you
                  confirm.
                </Typography>
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Checkbox
                      checked={authorized}
                      onChange={(event) =>
                        setAuthorized(event.target.checked)
                      }
                      disabled={loading}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: 12 }}>
                      I authorize The Pinnacle Companies to debit this account
                      for the amount shown.
                    </Typography>
                  }
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="success"
          disabled={
            loading ||
            !accountId ||
            !authorized ||
            invoices.length === 0 ||
            !amountsValid ||
            total <= 0
          }
          onClick={handleConfirm}
        >
          {loading ? "Processing..." : "Confirm & Pay"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CartPaymentDialog;
